/**
 * ============================================================
 * Wingman RC5 — Indoor Airport Intelligence
 * IndoorRouteEngine.ts
 *
 * Dijkstra-based indoor pedestrian route solver.
 * Computes shortest path on topology graph with meter-based
 * distances and level transition penalties.
 *
 * IAM-M16B-R1.6 — Minimum Production Route Engine
 * ============================================================
 */

import type {
  TopologyGraph,
  TopologyNode,
  TopologyEdge,
} from './IndoorTopologyContracts';
import { TopologyEdgeType } from './IndoorTopologyContracts';
import { haversineMeters } from './IndoorTopologyForensics';
import type {
  IndoorRouteRequest,
  IndoorRouteResponse,
  RouteSegment,
  RouteWaypoint,
  IIndoorRouteSolver,
  INodeResolver,
} from './IndoorRouteContracts';

// ── Node Resolver ────────────────────────────────────────────

/**
 * Resolves human-readable labels (e.g., "Gate B20", "TSA Security")
 * to topology graph node IDs using fuzzy matching.
 */
export class IndoorNodeResolver implements INodeResolver {
  resolve(label: string, graph: TopologyGraph, referenceCoords?: readonly [number, number]): TopologyNode | undefined {
    const normalized = label.toLowerCase().trim();

    // 1. Exact ID match
    const byId = graph.nodes.get(label);
    if (byId) return byId;

    // Helper to pick closest candidate if multiple candidates exist and referenceCoords is provided
    const pickBest = (candidates: TopologyNode[]): TopologyNode | undefined => {
      if (candidates.length === 0) return undefined;
      if (candidates.length === 1 || !referenceCoords) return candidates[0];
      let best = candidates[0];
      let minD = haversineMeters(candidates[0].coordinates, referenceCoords);
      for (let i = 1; i < candidates.length; i++) {
        const d = haversineMeters(candidates[i].coordinates, referenceCoords);
        if (d < minD) {
          minD = d;
          best = candidates[i];
        }
      }
      return best;
    };

    // 2. Exact label match (case-insensitive)
    const exactMatches: TopologyNode[] = [];
    for (const [, node] of graph.nodes) {
      if (node.label.toLowerCase() === normalized) {
        exactMatches.push(node);
      }
    }
    if (exactMatches.length > 0) return pickBest(exactMatches);

    // 3. Label contains query
    const containsMatches: TopologyNode[] = [];
    for (const [, node] of graph.nodes) {
      if (node.label.toLowerCase().includes(normalized)) {
        containsMatches.push(node);
      }
    }
    if (containsMatches.length > 0) return pickBest(containsMatches);

    // 4. Query contains node label
    const queryContainsMatches: TopologyNode[] = [];
    for (const [, node] of graph.nodes) {
      if (normalized.includes(node.label.toLowerCase())) {
        queryContainsMatches.push(node);
      }
    }
    if (queryContainsMatches.length > 0) return pickBest(queryContainsMatches);

    // 5. ID-based partial match
    for (const [id, node] of graph.nodes) {
      if (id.toLowerCase().includes(normalized)) {
        return node;
      }
    }

    return undefined;
  }
}

// ── Min-Heap Priority Queue ──────────────────────────────────

interface HeapEntry {
  nodeId: string;
  priority: number;
}

class MinHeap {
  private heap: HeapEntry[] = [];

  get size(): number {
    return this.heap.length;
  }

  insert(nodeId: string, priority: number): void {
    this.heap.push({ nodeId, priority });
    this.bubbleUp(this.heap.length - 1);
  }

  extractMin(): HeapEntry | undefined {
    if (this.heap.length === 0) return undefined;
    const min = this.heap[0];
    const last = this.heap.pop()!;
    if (this.heap.length > 0) {
      this.heap[0] = last;
      this.sinkDown(0);
    }
    return min;
  }

  private bubbleUp(i: number): void {
    while (i > 0) {
      const parent = Math.floor((i - 1) / 2);
      if (this.heap[parent].priority <= this.heap[i].priority) break;
      [this.heap[parent], this.heap[i]] = [this.heap[i], this.heap[parent]];
      i = parent;
    }
  }

  private sinkDown(i: number): void {
    const length = this.heap.length;
    while (true) {
      let smallest = i;
      const left = 2 * i + 1;
      const right = 2 * i + 2;

      if (left < length && this.heap[left].priority < this.heap[smallest].priority) {
        smallest = left;
      }
      if (right < length && this.heap[right].priority < this.heap[smallest].priority) {
        smallest = right;
      }
      if (smallest === i) break;
      [this.heap[smallest], this.heap[i]] = [this.heap[i], this.heap[smallest]];
      i = smallest;
    }
  }
}

// ── Dijkstra Route Solver ────────────────────────────────────

export class IndoorRouteEngine implements IIndoorRouteSolver {
  private readonly resolver: INodeResolver;

  constructor(resolver?: INodeResolver) {
    this.resolver = resolver ?? new IndoorNodeResolver();
  }

  /**
   * Compute the shortest indoor route between two nodes.
   *
   * Uses Dijkstra's algorithm with a min-heap priority queue.
   * Edge weights = Haversine distance + level transition penalty.
   *
   * Time complexity: O((V + E) log V)
   * Space complexity: O(V)
   */
  solve(
    graph: TopologyGraph,
    originId: string,
    destinationId: string,
  ): IndoorRouteResponse {
    const startTime = performance.now();

    // Validate graph
    if (graph.nodeCount === 0) {
      return this.errorResponse('Empty topology graph', startTime);
    }

    // Resolve destination first or origin first
    let destNode = graph.nodes.get(destinationId)
      ?? this.resolver.resolve(destinationId, graph);

    // Resolve origin, passing destNode coordinates if available to disambiguate multi-terminal POIs
    let originNode = graph.nodes.get(originId)
      ?? this.resolver.resolve(originId, graph, destNode ? destNode.coordinates : undefined);

    // If destination was not found initially, re-resolve with originNode coordinates
    if (!destNode && originNode) {
      destNode = this.resolver.resolve(destinationId, graph, originNode.coordinates);
    }

    if (!originNode) {
      return this.errorResponse(`Origin not found: "${originId}"`, startTime);
    }

    if (!destNode) {
      return this.errorResponse(`Destination not found: "${destinationId}"`, startTime);
    }

    // Same node
    if (originNode.id === destNode.id) {
      return this.sameNodeResponse(originNode, startTime);
    }

    // ── Dijkstra ──
    const dist = new Map<string, number>();
    const prev = new Map<string, string>();
    const prevEdge = new Map<string, TopologyEdge>();
    const visited = new Set<string>();

    // Initialize
    for (const [nodeId] of graph.nodes) {
      dist.set(nodeId, Infinity);
    }
    dist.set(originNode.id, 0);

    const heap = new MinHeap();
    heap.insert(originNode.id, 0);

    while (heap.size > 0) {
      const { nodeId: currentId } = heap.extractMin()!;

      if (visited.has(currentId)) continue;
      visited.add(currentId);

      // Early exit if we reached destination
      if (currentId === destNode.id) break;

      const neighbors = graph.adjacencyList.get(currentId) ?? [];
      for (const edge of neighbors) {
        if (visited.has(edge.to)) continue;

        const newDist = dist.get(currentId)! + edge.weight;
        if (newDist < (dist.get(edge.to) ?? Infinity)) {
          dist.set(edge.to, newDist);
          prev.set(edge.to, currentId);
          prevEdge.set(edge.to, edge);
          heap.insert(edge.to, newDist);
        }
      }
    }

    // ── Reconstruct path ──
    if (!visited.has(destNode.id) || dist.get(destNode.id) === Infinity) {
      return this.errorResponse(
        `No path exists from "${originNode.label}" to "${destNode.label}"`,
        startTime,
      );
    }

    const path: string[] = [];
    let current: string | undefined = destNode.id;
    while (current !== undefined) {
      path.unshift(current);
      current = prev.get(current);
    }

    // ── Build segments ──
    const segments: RouteSegment[] = [];
    const levelsTraversed = new Set<number>();
    let levelTransitions = 0;

    for (let i = 0; i < path.length - 1; i++) {
      const fromNode = graph.nodes.get(path[i])!;
      const toNode = graph.nodes.get(path[i + 1])!;
      const edge = prevEdge.get(path[i + 1])!;

      levelsTraversed.add(fromNode.level);
      levelsTraversed.add(toNode.level);

      if (edge.type === TopologyEdgeType.LEVEL_TRANSITION) {
        levelTransitions++;
      }

      segments.push({
        from: fromNode.id,
        to: toNode.id,
        edgeType: edge.type,
        distanceMeters: edge.distanceMeters,
        level: fromNode.level,
        instruction: this.generateInstruction(fromNode, toNode, edge),
      });
    }

    const computeTimeMs = Math.round((performance.now() - startTime) * 100) / 100;

    return {
      success: true,
      origin: this.toWaypoint(originNode),
      destination: this.toWaypoint(destNode),
      segments,
      path,
      totalDistanceMeters: Math.round((dist.get(destNode.id) ?? 0) * 100) / 100,
      levelTransitions,
      levelsTraversed: Array.from(levelsTraversed).sort((a, b) => a - b),
      computeTimeMs,
    };
  }

  /**
   * Resolve a label to a topology node.
   * Public accessor for the node resolver.
   */
  resolveNode(label: string, graph: TopologyGraph): TopologyNode | undefined {
    return graph.nodes.get(label) ?? this.resolver.resolve(label, graph);
  }

  // ── Private Helpers ──────────────────────────────────────

  private generateInstruction(
    from: TopologyNode,
    to: TopologyNode,
    edge: TopologyEdge,
  ): string {
    const dist = Math.round(edge.distanceMeters);

    switch (edge.type) {
      case TopologyEdgeType.LEVEL_TRANSITION:
        const direction = to.level > from.level ? 'up' : 'down';
        return `Take ${to.type} ${direction} from Level ${from.level} to Level ${to.level} (${dist}m)`;
      case TopologyEdgeType.DOOR_CONNECTION:
        return `Pass through ${from.label} toward ${to.label} (${dist}m)`;
      case TopologyEdgeType.ADJACENCY:
        return `Continue along ${to.label} (${dist}m)`;
      default:
        return `Walk to ${to.label} (${dist}m)`;
    }
  }

  private toWaypoint(node: TopologyNode): RouteWaypoint {
    return {
      nodeId: node.id,
      label: node.label,
      coordinates: node.coordinates,
      level: node.level,
    };
  }

  private errorResponse(error: string, startTime: number): IndoorRouteResponse {
    return {
      success: false,
      origin: null,
      destination: null,
      segments: [],
      path: [],
      totalDistanceMeters: 0,
      levelTransitions: 0,
      levelsTraversed: [],
      computeTimeMs: Math.round((performance.now() - startTime) * 100) / 100,
      error,
    };
  }

  private sameNodeResponse(node: TopologyNode, startTime: number): IndoorRouteResponse {
    return {
      success: true,
      origin: this.toWaypoint(node),
      destination: this.toWaypoint(node),
      segments: [],
      path: [node.id],
      totalDistanceMeters: 0,
      levelTransitions: 0,
      levelsTraversed: [node.level],
      computeTimeMs: Math.round((performance.now() - startTime) * 100) / 100,
    };
  }
}
